import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### メッセージ送信テンプレート API 操作クラス
 */
export default class SendMessageTemplates {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET SendMessageTemplate
   */
  public async get(userStore: LoginUser, templateId: number): Promise<SendMessageTemplatesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      templateId: templateId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageTemplate, formatParams);
    const response = await this.commonService.executeGet<SendMessageTemplatesGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET SendMessageTemplates
   */
  public async gets(userStore: LoginUser, queryParams?: { startIndex: number; endIndex: number }): Promise<Array<SendMessageTemplatesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageTemplates, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<SendMessageTemplatesGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      params,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * POST SendMessageTemplate
   */
  public async post(userStore: LoginUser, requestBody: SendMessageTemplatesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageTemplates, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH SendMessageTemplates
   */
  public async patch(userStore: LoginUser, templateId: number, requestBody: SendMessageTemplatesPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      templateId: templateId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageTemplate, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE SendMessageTemplates
   */
  public async delete(userStore: LoginUser, templateId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      templateId: templateId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageTemplate, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface SendMessageTemplatesGetModel {
  /** 施設を特定するためのID */
  facilityId: number;
  /** 一意に識別する ID */
  id: number;
  /** テンプレート種別 */
  templateType: TemplateType;
  /** グループ名 */
  name: string;
  /** メッセージ送信で転記するタイトル */
  title: string | null;
  /** メッセージ送信で転記する本文 */
  text: string | null;
  /** メッセージ送信で見ましたボタンをつけるか */
  addOptionText: boolean;
  /** メッセージ送信で既読確認メッセージボタンをつけるか */
  addReadButton: boolean;
  /** メッセージ送信で返信ボタンをつけるか */
  addReplyButton: boolean;
  /** 記載者名 */
  writtenBy: string;
  /** 記載者ID */
  writerId: string;
  /** 削除フラグ */
  deleteFlag: boolean;
  /** レコード作成日 */
  created: number;
  /** レコード更新日 */
  updated: number;
}

export enum TemplateType {
  /** メッセージ送信 */
  MESSAGE = "message",
  /** 待機者メッセージ送信 */
  WAITER_MESSAGE = "waiterMessage"
}

export interface SendMessageTemplatesPostModel {
  /** テンプレート種別 */
  templateType: TemplateType;
  /** テンプレート名 */
  name: string;
  /** メッセージ送信で転記するタイトル */
  title: string | null;
  /** メッセージ送信で転記する本文 */
  text: string | null;
  /** メッセージ送信で見ましたボタンをつけるか */
  addOptionText: boolean;
  /** メッセージ送信で既読確認メッセージボタンをつけるか */
  addReadButton: boolean;
  /** メッセージ送信で返信ボタンをつけるか */
  addReplyButton: boolean;
}

export interface SendMessageTemplatesPatchModel {
  /** テンプレート種別 */
  templateType: TemplateType;
  /** テンプレート名 */
  name: string;
  /** メッセージ送信で転記するタイトル */
  title: string | null;
  /** メッセージ送信で転記する本文 */
  text: string | null;
  /** メッセージ送信で見ましたボタンをつけるか */
  addOptionText: boolean;
  /** メッセージ送信で既読確認メッセージボタンをつけるか */
  addReadButton: boolean;
  /** メッセージ送信で返信ボタンをつけるか */
  addReplyButton: boolean;
  /** 更新日時 */
  updated: number;
}
